import { useApi, useEnvApi } from './useApi'

/**
 * 描述: 获取当前地理位置
 * @param {Object} data - 入参需要以对象的形式
 * @param {Function} data.onSuccess - 成功回调
 * @param {Function} data.onFail - 失败回调
 * @returns 包含经纬度等信息的对象
 * @example <caption>地理位置</caption>
 * utils.xbbGetLocation({
 *   onSuccess: res => {  }, // 包含经纬度等信息的对象
 *   onFail: e => {}
 * })
 */
const xbbGetLocation = (data) => useApi({ name: 'xbbGetLocation', data }, ['onSuccess', 'onFail'])

/**
 * 描述：开始录音
 * @param {Object}  data - 入参需要以对象的形式，【】代表某个环境的特殊参数
 * @param {Function} data.drawRecord - 开始录音的动画
 * @param {Function} [data.recordAutoEnd] - 【dd/wx】监听录音自动停止
 * @param {Function} [data.onSuccess] - 【h5】成功回调，返回包含音频id的对象
 * @returns {Object} {mediaId} - 音频id
 * @example <caption>开始录音</caption>
 * utils.xbbRecordStart({
 *  drawRecord: () => {},
 *  recordAutoEnd: () => {},
 *  onSuccess: res => console.log(res.mediaId) //独立版音频id
 * })
 */
const xbbRecordStart = (data) => useApi({ name: 'xbbRecordStart', data })

/**
 * 描述：结束录音
 * @date 2021-05-11
 * @param {Object}  data - 入参需要以对象的形式
 * @param {Function} data.onSuccess - 成功回调
 * @param {Function} data.onFail - 失败回调，包含音频id、音频oss链接的对象
 * @returns {Object} {mediaId,ossUrl} - 音频id，oss链接
 * @example <caption>结束录音</caption>
 * utils.xbbRecordStop({
 *   onSuccess: res => {
 *     console.log(res.mediaId), // 音频id
 *     console.log(res.ossUrl), // 企业微信、独立版音频的oss链接
 *   }
 *   onFail: ()=>{}
 * })
 */
const xbbRecordStop = (data) => useApi({ name: 'xbbRecordStop', data })

/**
 * 描述：暂停录音
 */
const xbbRecordPause = () => useApi({ name: 'xbbRecordPause' })

/**
 * 描述：继续录音
 */
const xbbRecordResume = () => useApi({ name: 'xbbRecordResume' })

/**
 * 描述：设置本地存储
 */
const xbbLocalStorageSet = (key, value, expired) =>
  useApi({
    name: 'xbbLocalStorageSet',
    data: {
      key,
      value,
      expired
    }
  })

/**
 * 描述：获取本地存储
 */
const xbbLocalStorageGet = (key) => useApi({ name: 'xbbLocalStorageGet', data: { key } })
/**
 * 描述：删除本地存储
 */
const xbbLocalStorageRemove = (key) => useApi({ name: 'xbbLocalStorageRemove', data: { key } })

/**
 * 描述：清空本地存储
 */
const xbbLocalStorageClear = () => useApi({ name: 'xbbLocalStorageClear' })

/**
 * 描述：h5初始化加载
 * @param
 * @example <caption>h5初始化加载</caption>
 * utils.h5HandlePlusready()
 */
const h5HandlePlusready = (route, router) =>
  useEnvApi({ name: 'h5HandlePlusready', data: { route, router } })

/**
 * 描述：处理返回键的判断
 */
const h5GoBack = () => useEnvApi({ name: 'h5GoBack' })

/**
 * 描述：热更新
 */
const throttleH5ResourceUpgrade = () => useEnvApi({ name: 'throttleH5ResourceUpgrade' })

/**
 * 描述：获取指定APPID对应的应用信息
 * @see {@link https://www.html5plus.org/doc/zh_cn/runtime.html#plus.runtime.GetPropertyCallBack wgtinfo对象参数}
 * @param {Function} callBack - 回调函数
 * @example <caption>获取信息</caption>
 * utils.h5GetProperty(callBack: wgtinfo=>{ console.log(wgtinfo) } )
 * @returns {Object} 回调函数返回应用信息=》wgtinfo - 详见对象参数链接
 */
const h5GetProperty = (callBack) => useApi({ name: 'h5GetProperty', data: callBack })

/**
 * 描述: 打开外部链接
 * @param {Object} data - 入参需要以对象的形式
 * @param {String} data.url - 需要打开的网页链接
 * @example <caption>打开外部链接</caption>
 * utils.xbbOpenLink({ url: '' })
 */
const xbbOpenLink = (data) => useApi({ name: 'xbbOpenLink', data })

/**
 * 描述：下载文件
 * @param {Object}  data - 入参需要以对象的形式
 * @param {String}  data.url - 要下载文件资源地址
 * @param {Function} data.onSuccess - 成功回调
 * @param {Function} data.onFail - 失败回调
 * @returns {String} 成功回调返回文件名称=》filename
 * @example <caption>下载文件</caption>
 * utils.h5DownLoadFile({ url: '', onSuccess: filename=>{ console.log(filename) }, onFail: _=>{} })
 */
const h5DownLoadFile = (data) => useApi({ name: 'h5DownLoadFile', data })

/**
 * 描述：安装文件
 * @param {Object}  data - 入参需要以对象的形式
 * @param {String}  data.url - 要下载文件资源地址
 * @param {Function} data.onSuccess - 成功回调
 * @param {Function} data.onFail - 失败回调
 * @example <caption>安装文件</caption>
 * utils.h5InstallApp({ name: '', onSuccess: _=>{}, onFail: _=>{} })
 */
const h5InstallApp = (data) => useApi({ name: 'h5InstallApp', data })

/**
 * 描述：拨打电话
 * @param {Object}  data - 入参需要以对象的形式
 * @param {String}  data.phoneNumber - 要拨打的号码
 * @param {Function} data.success - 成功回调
 * @param {Function} data.fail - 失败回调
 */
const xbbDial = (data) => useApi({ name: 'xbbDial', data })

export default {
  xbbGetLocation,
  xbbRecordStart,
  xbbRecordStop,
  xbbRecordPause,
  xbbRecordResume,
  xbbLocalStorageSet,
  xbbLocalStorageGet,
  xbbLocalStorageRemove,
  xbbLocalStorageClear,
  h5HandlePlusready,
  h5GoBack,
  throttleH5ResourceUpgrade,
  h5GetProperty,
  xbbOpenLink,
  h5DownLoadFile,
  h5InstallApp,
  xbbDial
}
